/**
 * Reactive façade around `AutoCourtCalibrator` that supports LIVE
 * re-calibration: callers feed the current frame periodically (every few
 * seconds), and we EMA-blend successive successful homographies to avoid
 * jittery overlays. A new result is rejected outright if its confidence is
 * much lower than the running one.
 *
 * Intentionally plain-object state (not Svelte runes) so this module can be
 * unit-tested in vitest without a Svelte harness. Callers that want
 * reactivity wrap `state` in `$state(...)` themselves.
 */

import type { Matrix3x3 } from '@tbc/core/court';
import {
  AutoCourtCalibrator,
  type CalibratorFrame,
  type CalibrationResult,
} from './autoCourtCalibrator.js';

export interface CourtLiveState {
  /** 4 pixel corners in original-frame coords. Null until a successful calibration. */
  corners: readonly [number, number][] | null;
  /** Pixel -> ITF doubles metres homography, once detected. */
  homography: Matrix3x3 | null;
  /** Rolling confidence of the current `homography`. */
  confidence: number;
  /** Monotonically increasing count of successful calibrations so far. */
  successCount: number;
  /** Monotonically increasing count of failed calibration attempts since
   *  the last success (or since session reset). */
  consecutiveFailures: number;
  /** Last calibration attempt's failure reason, if any. */
  lastError: string | null;
}

export interface CourtLiveStateHandle {
  readonly state: CourtLiveState;
  /** Runs the calibrator once. Returns true on success (accepted new result). */
  tryCalibrate(frame: CalibratorFrame): boolean;
  /** Reset all state. */
  reset(): void;
}

/** EMA weight for newly-accepted calibrations (rest goes to the running H). */
const EMA_NEW = 0.3;
/**
 * If a new result's confidence is less than this fraction of the current,
 * we discard it — avoids a bad lock-on overwriting a solid calibration.
 */
const MIN_RELATIVE_CONFIDENCE = 0.6;

export function createCourtLiveState(
  calibrator: AutoCourtCalibrator = new AutoCourtCalibrator(),
): CourtLiveStateHandle {
  const state: CourtLiveState = {
    corners: null,
    homography: null,
    confidence: 0,
    successCount: 0,
    consecutiveFailures: 0,
    lastError: null,
  };

  function tryCalibrate(frame: CalibratorFrame): boolean {
    let result: CalibrationResult | null;
    try {
      result = calibrator.calibrate(frame);
    } catch (err) {
      state.lastError = err instanceof Error ? err.message : String(err);
      state.consecutiveFailures += 1;
      return false;
    }
    if (result === null) {
      state.lastError = 'no-court-detected';
      state.consecutiveFailures += 1;
      return false;
    }
    // Reject a severely degraded result: keep the good one we already have.
    if (
      state.homography !== null &&
      result.confidence < state.confidence * MIN_RELATIVE_CONFIDENCE
    ) {
      state.lastError = 'low-confidence';
      state.consecutiveFailures += 1;
      return false;
    }

    if (state.homography === null) {
      state.homography = result.homography;
      state.corners = result.corners;
      state.confidence = result.confidence;
    } else {
      state.homography = blendHomography(state.homography, result.homography, EMA_NEW);
      state.corners = blendCorners(state.corners!, result.corners, EMA_NEW);
      state.confidence =
        state.confidence * (1 - EMA_NEW) + result.confidence * EMA_NEW;
    }
    state.lastError = null;
    state.successCount += 1;
    state.consecutiveFailures = 0;
    return true;
  }

  function reset(): void {
    state.corners = null;
    state.homography = null;
    state.confidence = 0;
    state.successCount = 0;
    state.consecutiveFailures = 0;
    state.lastError = null;
  }

  return { state, tryCalibrate, reset };
}

/**
 * Element-wise EMA of two homographies, with post-normalisation so h33 = 1.
 * Averaging homographies directly is mathematically loose (they live on a
 * projective manifold) but the elements change smoothly over successive
 * broadcast frames and this is good enough to damp overlay jitter.
 */
function blendHomography(prev: Matrix3x3, next: Matrix3x3, alpha: number): Matrix3x3 {
  const a = 1 - alpha;
  const raw: number[] = [];
  for (let i = 0; i < 9; i++) raw.push(prev[i]! * a + next[i]! * alpha);
  const scale = Math.abs(raw[8]!) > 1e-12 ? 1 / raw[8]! : 1;
  return [
    raw[0]! * scale, raw[1]! * scale, raw[2]! * scale,
    raw[3]! * scale, raw[4]! * scale, raw[5]! * scale,
    raw[6]! * scale, raw[7]! * scale, 1,
  ];
}

function blendCorners(
  prev: readonly [number, number][],
  next: readonly [number, number][],
  alpha: number,
): [number, number][] {
  const n = Math.min(prev.length, next.length);
  const out: [number, number][] = [];
  const a = 1 - alpha;
  for (let i = 0; i < n; i++) {
    out.push([
      prev[i]![0] * a + next[i]![0] * alpha,
      prev[i]![1] * a + next[i]![1] * alpha,
    ]);
  }
  return out;
}
